import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogPanel,
  Button,
  DateRangePicker,
  NumberInput,
  Textarea,
  Title,
} from '@tremor/react';
import { DateRangePickerValue } from '@tremor/react';
import timesheets from '../../../network/timesheets';
import { useToast } from '../../../utils/useToast';
import { format, differenceInMinutes, isValid } from 'date-fns';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface CreateTimesheetModalProps {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  onSuccess: () => void;
}

// Helper function to combine date and time string into a Date object
const combineDateAndTime = (datePart: Date | undefined, timePart: string): Date | null => {
  if (!datePart || !timePart) return null;
  const [hours, minutes] = timePart.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return null;

  const combined = new Date(datePart);
  combined.setHours(hours, minutes, 0, 0); // Set hours and minutes
  return combined;
};

export function CreateTimesheetModal({
  isOpen,
  setOpen,
  onSuccess,
}: CreateTimesheetModalProps) {
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [tasksWorked, setTasksWorked] = useState('');
  const [breakTime, setBreakTime] = useState<number | undefined>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleDateChange = (value: DateRangePickerValue) => {
     if (value.from) {
        setDate(value.from);
     } else {
        setDate(undefined);
     }
  };

  // Calculate duration preview (optional, but helpful for UX)
  const calculatedDuration = useMemo(() => {
    const startDateTime = combineDateAndTime(date, startTime);
    const endDateTime = combineDateAndTime(date, endTime);

    if (startDateTime && endDateTime && isValid(startDateTime) && isValid(endDateTime) && endDateTime > startDateTime) {
        const totalMinutes = differenceInMinutes(endDateTime, startDateTime);
        const netMinutes = Math.max(0, totalMinutes - (breakTime || 0));
        const hours = Math.floor(netMinutes / 60);
        const minutes = netMinutes % 60;
        return `${hours}h ${minutes}m`;
    }
    return null;
  }, [date, startTime, endTime, breakTime]);

  const resetForm = () => {
    setDate(new Date());
    setStartTime('');
    setEndTime('');
    setTasksWorked('');
    setBreakTime(0);
    setIsSubmitting(false);
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const startDateTime = combineDateAndTime(date, startTime);
    const endDateTime = combineDateAndTime(date, endTime);

    // --- Basic Validation ---
    if (!date || !startTime || !endTime) {
        toast({
            variant: 'warning',
            title: 'Please fill in Date, Start Time, and End Time.',
        });
        return;
    }

    if (!startDateTime || !endDateTime || !isValid(startDateTime) || !isValid(endDateTime)) {
         toast({
             variant: 'warning',
             title: 'Invalid start or end time format. Please use HH:mm (e.g., 09:00).',
         });
         return;
    }

    // --- Common Sense Validations ---

    // 1. Date Range Check (Not too far in the past)
    const earliestAllowedDate = new Date();
    earliestAllowedDate.setDate(earliestAllowedDate.getDate() - 90); // Allow last 90 days
    earliestAllowedDate.setHours(0, 0, 0, 0); // Normalize time part for comparison

    if (date < earliestAllowedDate) {
        toast({
            variant: 'warning',
            title: 'Invalid Date',
            description: 'Timesheet date cannot be more than 90 days in the past.',
        });
        return;
    }

    // 2. End Time After Start Time
    if (endDateTime <= startDateTime) {
        toast({
            variant: 'warning',
            title: 'Invalid Time Range',
            description: 'End Time must be after Start Time.',
        });
        return;
    }

    // 3. Maximum Duration Check
    const totalMinutes = differenceInMinutes(endDateTime, startDateTime);
    if (totalMinutes > 24 * 60) { // Max 24 hours for a single entry
        toast({
            variant: 'warning',
            title: 'Duration Too Long',
            description: 'The time difference cannot exceed 24 hours for a single entry.',
        });
        return;
    }

    // 4. Break Time Check (Cannot exceed total time)
    const netMinutes = Math.max(0, totalMinutes - (breakTime || 0));
    const calculatedHoursWorked = parseFloat((netMinutes / 60).toFixed(2));

    // Check if break time makes worked hours zero or negative when there was actual time difference
    if (calculatedHoursWorked <= 0 && totalMinutes > 0) {
         toast({
            variant: 'warning',
            title: 'Invalid Break Time',
            description: 'Break time cannot equal or exceed the total work duration.',
         });
         return;
     }

    // 5. Trim Tasks Worked Input
    const trimmedTasks = tasksWorked.trim();
    // Note: We are not making tasks mandatory here, but sending the trimmed version.
    // Add `if (!trimmedTasks) { ... }` if it should be required.


    // --- Submission ---
    setIsSubmitting(true);
    try {
      // Construct the payload according to the backend DTO
      const payload = {
        date: format(date, 'yyyy-MM-dd'),
        worktime: `${startTime} - ${endTime}`, // Combine start and end time
        notes: trimmedTasks, // Use tasksWorked input as notes
        breaktime: (breakTime || 0).toString(), // Convert breaktime to string
      };

      await timesheets.createTimesheet(payload);
      toast({
        variant: 'success',
        title: 'Timesheet entry created successfully!',
      });
      resetForm();
      setOpen(false);
      onSuccess();
    } catch (error) {
      console.error('Failed to create timesheet:', error);
      toast({
        variant: 'error',
        title: 'Failed to create timesheet.',
        description: 'Something went wrong. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={(val) => setOpen(val)} static={true}>
      <DialogPanel>
        <Title className="mb-3">Create Timesheet Entry</Title>
         <button
              onClick={() => setOpen(false)}
              className="absolute right-3 top-3 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Close"
          >
              <XMarkIcon className="h-5 w-5" />
          </button>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 mt-4">
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong mb-1">Date</label>
              <DateRangePicker
                value={{ from: date }}
                onValueChange={handleDateChange}
                enableSelect={false}
                maxDate={new Date()}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="startTime" className="block text-sm font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong mb-1">Start Time</label>
                  <input
                    type="time"
                    id="startTime"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    required
                    className="mt-1 block w-full rounded-tremor-default border border-tremor-border p-2 text-tremor-content shadow-tremor-input focus:border-tremor-brand-subtle focus:outline-none focus:ring-1 focus:ring-tremor-brand-subtle dark:border-dark-tremor-border dark:bg-dark-tremor-background dark:text-dark-tremor-content dark:shadow-dark-tremor-input dark:focus:border-dark-tremor-brand-subtle dark:focus:ring-dark-tremor-brand-subtle"
                  />
                </div>
                <div>
                  <label htmlFor="endTime" className="block text-sm font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong mb-1">End Time</label>
                  <input
                    type="time"
                    id="endTime"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    required
                     className="mt-1 block w-full rounded-tremor-default border border-tremor-border p-2 text-tremor-content shadow-tremor-input focus:border-tremor-brand-subtle focus:outline-none focus:ring-1 focus:ring-tremor-brand-subtle dark:border-dark-tremor-border dark:bg-dark-tremor-background dark:text-dark-tremor-content dark:shadow-dark-tremor-input dark:focus:border-dark-tremor-brand-subtle dark:focus:ring-dark-tremor-brand-subtle"
                 />
                </div>
            </div>
            <div>
              <label htmlFor="breakTime" className="block text-sm font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong mb-1">Break Time (minutes)</label>
              <NumberInput
                id="breakTime"
                placeholder="e.g., 30"
                value={breakTime}
                onValueChange={setBreakTime}
                min={0}
                step={5}
                enableStepper={false}
                className="mt-1"
              />
            </div>
            {calculatedDuration && (
                <p className="text-sm text-tremor-content dark:text-dark-tremor-content mt-1">
                    Calculated work duration: {calculatedDuration}
                </p>
             )}
            <div>
              <label htmlFor="tasksWorked" className="block text-sm font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong mb-1">Tasks Worked</label>
              <Textarea
                id="tasksWorked"
                placeholder="Describe the tasks you performed..."
                value={tasksWorked}
                onChange={(e) => setTasksWorked(e.target.value)}
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-2">
              <Button
                  variant="secondary"
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={isSubmitting}
              >
                  Cancel
              </Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                  Submit Entry
              </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
} 